import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { useParams } from 'react-router-dom';
import { makeStyles, Typography } from '@material-ui/core';
import { getLatestHighlight } from './apiQueries';
import HighlightDetails from '../highlights/HighlightDetails';
import DateCell from '../common/DateCell';
import { Highlight } from '../../utils/types';

const timeNow = (): JSX.Element => {
	const now = new Date().toISOString();
	return <DateCell value={now} />;
};

const useStyles = makeStyles((theme) => ({
	refreshNotice: {
		marginBottom: theme.spacing(2)
	}
}));

const LatestHighlight: React.FC = () => {
	const { id }: LatestHighlightParams = useParams();
	const classes = useStyles();
	const [highlightData, setHighlightData] = useState<Highlight | null>(null);
	const pollInterval = 15000;

	const { loading, error, data } = useQuery(getLatestHighlight, {
		variables: {
			airingId: decodeURIComponent(id),
			type: 'LIVE',
			available: true
		},
		pollInterval,
		notifyOnNetworkStatusChange: true
	});

	useEffect(() => {
		if (data?.getLatestHighlight) {
			setHighlightData(data.getLatestHighlight);
		}
	}, [data]);

	if (loading && !highlightData) return <Typography>Loading...</Typography>;
	if (error) return <Typography>Error loading data from the server</Typography>;
	if (!data?.getLatestHighlight) return <Typography>No highlight found</Typography>;

	return (
		<>
			<Typography variant='body2' className={classes.refreshNotice}>
				This page refreshes every {pollInterval / 1000} secs and was last updated at: {timeNow()}
			</Typography>
			{highlightData ? (
				<MemoisedHighlightDetails
					available={highlightData.available}				
					drm={highlightData.drm}
				    sources={highlightData.sources}					
				    game={highlightData.game}
					gameId={highlightData.game?.id}
					id={highlightData.id}
					periods={highlightData.periods}
					publishTime={highlightData.publishTime}
					type={highlightData.type}
					videoAssets={highlightData.videoAssets}
					availableTime={highlightData.availableTime}
				/>
			) : (
				<Typography>Game has no highlights</Typography>
			)}
		</>
	);
};

const checkHighlightPropsEquality = (prevHighlight: Highlight, nextHighlight: Highlight): boolean =>
	prevHighlight.id === nextHighlight.id && JSON.stringify(prevHighlight.videoAssets) === JSON.stringify(nextHighlight.videoAssets);

const MemoisedHighlightDetails = React.memo(HighlightDetails, checkHighlightPropsEquality);

export default LatestHighlight;

type LatestHighlightParams = { id: string };
